"use client";

import Image from "next/image";
import { FadeInUp, StaggerChildren, StaggerItem } from "./AnimatedSection";

const features = [
  {
    num: "01",
    title: "Unlimited Revisions",
    description: "We're committed to your satisfaction with unlimited revisions at every step. Your vision, exactly as you imagine.",
    icon: "/assets/why-choose-us/Infinity.avif",
  },
  {
    num: "02",
    title: "24/7 Availability",
    description: "We respond fast so your project never stalls — no matter your timezone. Round the clock, every day.",
    icon: "/assets/why-choose-us/Profile.avif",
  },
  {
    num: "03",
    title: "Affordable Plans",
    description: "Top-quality service without breaking the bank. 30% below market rates with transparent pricing.",
    icon: "/assets/why-choose-us/Dollar.avif",
  },
  {
    num: "04",
    title: "Custom Solutions",
    description: "No cookie-cutter templates. Every project is tailored to your specific needs and requirements.",
    icon: "/assets/why-choose-us/Diagram.avif",
  },
  {
    num: "05",
    title: "Lifetime Support",
    description: "We're there at every stage with guidance and assistance whenever you need it. You're never alone.",
    icon: "/assets/why-choose-us/Star.avif",
  },
];

function FeatureCard({ f }: { f: typeof features[0] }) {
  return (
    <div className="bg-surface-light border border-card-border rounded-2xl p-5 sm:p-6 md:p-7 hover:border-foreground/10 transition-all duration-300 group h-full">
      <div className="flex items-start gap-4 h-full">
        <div className="flex-shrink-0 w-12 h-12 relative">
          <Image src={f.icon} alt={f.title} fill className="object-contain object-left theme-invert opacity-80 group-hover:opacity-100 transition-opacity" />
        </div>
        <div className="flex-1 flex flex-col h-full">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-base font-bold font-ui group-hover:text-foreground transition-colors">{f.title}</h3>
            <span className="text-xs font-bold font-ui text-badge-text">{f.num}</span>
          </div>
          <p className="text-xs text-foreground/50 sm:text-foreground/40 font-body leading-relaxed flex-1">{f.description}</p>
        </div>
      </div>
    </div>
  );
}

export default function WhyChooseUs() {
  return (
    <section className="py-12 sm:py-16 md:py-20 bg-surface">
      <div className="max-w-[960px] mx-auto px-6">
        <FadeInUp>
          <div className="text-center mb-14">
            <p className="section-badge mb-4 mx-auto">What Sets Us Apart</p>
            <h2 className="text-2xl sm:text-3xl md:text-[48px] font-bold leading-[1.2] font-ui">
              <span>We Design for the </span>
              <span className="font-serif italic text-purple-light">Future</span>
              <span> to Drive Today&apos;s </span>
              <span className="font-serif italic text-purple-light">Success</span>
            </h2>
          </div>
        </FadeInUp>

        {/* Row 1: 2 items */}
        <StaggerChildren className="grid sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4" staggerDelay={0.1}>
          {features.slice(0, 2).map((f, i) => (
            <StaggerItem key={i}>
              <FeatureCard f={f} />
            </StaggerItem>
          ))}
        </StaggerChildren>

        {/* Row 2: 3 items */}
        <StaggerChildren className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4" staggerDelay={0.1}>
          {features.slice(2).map((f, i) => (
            <StaggerItem key={i}>
              <FeatureCard f={f} />
            </StaggerItem>
          ))}
        </StaggerChildren>
      </div>
    </section>
  );
}
